import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import LayerDriftSeverityBadge from "@/v2-backoffice-client/layer/drift/LayerDriftSeverityBadge"
import LayerCountDriftAcceptButton from "@/v2-backoffice-client/layer/drift/accept/LayerCountDriftAcceptButton"
import {
  asCountSeverity,
  formatCountDelta,
  type CountSeverity,
} from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import { formatRelativeTime } from "@/v2-backoffice-client/uptime/uptime.utils"
import type { LayerCountDriftFragment } from "@/v2-backoffice-client/uptime/dashboard/__types__/dashboard.driftSummaries.fragment"

export type LayerCountDriftBlockProps = ComponentProps<"section"> & {
  countDrift: LayerCountDriftFragment
  // When true (the default), the "accept current as baseline" action
  // renders alongside the block. The dashboard summary lists pass
  // false so the action only shows up on the detail surfaces where
  // an operator can deliberate.
  enableAccept?: boolean
  onAccepted?(): void
}

// LayerCountDriftBlock renders the feature-count drift surface for a
// single (Source, Identifier) target. It is the count-side sibling of
// the metadata `LayerDriftHeader + LayerDriftTable` pair: a small
// summary header with severity + accept-baseline action, followed by
// a tight stat row showing baseline count, current count, and the
// percentage delta.
//
// Renders unconditionally when given a populated CountDrift fragment
// so a "no drift" state still surfaces the captured baseline -- the
// operator's question on the detail page is "is the count steady?"
// not "is anything broken?", so an explicit OK readout is more
// useful than hiding the section.
function LayerCountDriftBlock(props: LayerCountDriftBlockProps) {
  const {
    countDrift,
    enableAccept = true,
    onAccepted,
    className,
    ...sectionProps
  } = props

  const severity = asCountSeverity(countDrift.Severity)
  const delta = formatCountDelta(
    countDrift.BaselineCount,
    countDrift.CurrentCount,
  )
  const filterDrifted =
    countDrift.BaselineAppliedWhere !== countDrift.CurrentAppliedWhere

  return (
    <section
      className={cn(
        "rounded-md border bg-card/40 px-4 py-3 flex flex-col gap-3",
        countDrift.HasCountDrift && severityBorderClass(severity),
        className,
      )}
      {...sectionProps}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <Icon
              src="tally"
              className={cn(
                countDrift.HasCountDrift
                  ? severityIconClass(severity)
                  : "text-muted-foreground",
              )}
            />
            {countDrift.HasCountDrift ? (
              <span className="font-medium">Feature-count drift detected</span>
            ) : (
              <span className="font-medium text-muted-foreground">
                Feature counts match baseline
              </span>
            )}
            {countDrift.HasCountDrift && severity !== "none" && (
              <LayerDriftSeverityBadge severity={severity}>
                {severity}
              </LayerDriftSeverityBadge>
            )}
          </div>
          <div className="text-xs text-muted-foreground space-x-3">
            <span>
              Baseline:{" "}
              {countDrift.BaselineSetAt
                ? formatRelativeTime(countDrift.BaselineSetAt)
                : countDrift.BaselineObservedAt
                  ? formatRelativeTime(countDrift.BaselineObservedAt)
                  : "—"}
            </span>
            {countDrift.HasCountDrift && countDrift.CountDriftDetectedAt && (
              <span className={severityTextClass(severity)}>
                Drifted: {formatRelativeTime(countDrift.CountDriftDetectedAt)}
              </span>
            )}
            <span>
              Last seen:{" "}
              {countDrift.CurrentObservedAt
                ? formatRelativeTime(countDrift.CurrentObservedAt)
                : "—"}
            </span>
          </div>
        </div>

        {enableAccept && countDrift.HasCountDrift && (
          <LayerCountDriftAcceptButton
            source={countDrift.Source}
            identifier={countDrift.Identifier}
            onCompleted={onAccepted}
          />
        )}
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Stat
          label="Baseline"
          value={formatCount(countDrift.BaselineCount)}
          tone="muted"
        />
        <Stat
          label="Current"
          value={formatCount(countDrift.CurrentCount)}
          tone={
            countDrift.HasCountDrift
              ? severityToneToTextTone(severity)
              : "muted"
          }
        />
        <Stat
          label="Δ vs baseline"
          value={delta?.pct ?? "—"}
          sub={delta != null ? formatAbsoluteDelta(delta.absolute) : undefined}
          tone={
            countDrift.HasCountDrift
              ? severityToneToTextTone(severity)
              : "muted"
          }
        />
      </div>

      {(countDrift.BaselineAppliedWhere || countDrift.CurrentAppliedWhere) && (
        <footer className="border-t pt-2 text-xs text-muted-foreground space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="shrink-0">Filter (where):</span>
            <code className="font-mono break-all">
              {countDrift.CurrentAppliedWhere ?? "—"}
            </code>
          </div>
          {filterDrifted && (
            <div className="flex items-baseline gap-2">
              <span className="shrink-0 text-amber-500">Baseline filter:</span>
              <code className="font-mono break-all">
                {countDrift.BaselineAppliedWhere ?? "—"}
              </code>
            </div>
          )}
        </footer>
      )}
    </section>
  )
}

type StatTone = "muted" | "warning" | "destructive"

function Stat(props: {
  label: string
  value: string
  sub?: string
  tone: StatTone
}) {
  const valueClass =
    props.tone === "destructive"
      ? "text-destructive"
      : props.tone === "warning"
        ? "text-amber-500"
        : "text-foreground"
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {props.label}
      </span>
      <span className={cn("text-base font-semibold tabular-nums", valueClass)}>
        {props.value}
      </span>
      {props.sub && (
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {props.sub}
        </span>
      )}
    </div>
  )
}

function formatCount(n: number | null | undefined): string {
  if (n == null) return "—"
  return formatNumber(n)
}

function formatAbsoluteDelta(diff: number): string {
  const sign = diff > 0 ? "+" : ""
  return `${sign}${formatNumber(diff)} features`
}

// Severity → tone mapping. "info" lives on the violet metadata-drift
// palette in this codebase, but for *count* drift it isn't currently
// emitted (severity goes none → warning → breaking) so the branch
// only exists to keep the union exhaustive.
function severityBorderClass(s: CountSeverity): string {
  switch (s) {
    case "breaking":
      return "border-destructive/40"
    case "warning":
      return "border-amber-500/40"
    case "info":
      return "border-violet-500/40"
    case "none":
      return ""
  }
}
function severityIconClass(s: CountSeverity): string {
  switch (s) {
    case "breaking":
      return "text-destructive"
    case "warning":
      return "text-amber-500"
    case "info":
      return "text-violet-500"
    case "none":
      return "text-muted-foreground"
  }
}
function severityTextClass(s: CountSeverity): string {
  switch (s) {
    case "breaking":
      return "text-destructive font-medium"
    case "warning":
      return "text-amber-500 font-medium"
    case "info":
      return "text-violet-500 font-medium"
    case "none":
      return "text-muted-foreground"
  }
}
function severityToneToTextTone(s: CountSeverity): StatTone {
  switch (s) {
    case "breaking":
      return "destructive"
    case "warning":
      return "warning"
    case "info":
      return "warning"
    case "none":
      return "muted"
  }
}

export default LayerCountDriftBlock
